import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { AttributeOption, DispositionsFormType, DispositionsForm } from '@mark43/cad-api';
import { VStack } from '@cad-arc';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';

// 'clear_event' endpoint expects all disposition values stored in `updateDispositionsRequest.form` object when unitLevelDispositionsEnabled = true.
const EVENT_CLEAR_DISPOSITION_FORM_FIELDS_PREFIX = 'updateDispositionsRequest.form.';

const getFieldName = (
    fieldName: string,
    isEventClearing?: boolean,
    unitLevelDispositionsEnabled?: boolean
) => {
    return isEventClearing && unitLevelDispositionsEnabled
        ? `${EVENT_CLEAR_DISPOSITION_FORM_FIELDS_PREFIX}${fieldName}`
        : fieldName;
};

const isPrimaryDispositionRequired = ({
    dispositionIsRequiredByAgency,
    unitLevelDispositionsEnabled,
    isPrimaryUnit,
    isEventClearing,
}: {
    dispositionIsRequiredByAgency: boolean;
    unitLevelDispositionsEnabled: boolean;
    isPrimaryUnit?: boolean;
    isEventClearing?: boolean;
}) => {
    if (!dispositionIsRequiredByAgency) {
        return false;
    }
    if (!unitLevelDispositionsEnabled) {
        return dispositionIsRequiredByAgency;
    }
    if (isEventClearing) {
        return dispositionIsRequiredByAgency;
    }
    return dispositionIsRequiredByAgency && !!isPrimaryUnit;
};

export function DispositionForm({
    isLoading,
    agencyId,
    dispositionOptions,
    isPrimaryUnit,
    initialValues,
    isEventClearing,
    primaryDispositionEditable = true,
}: {
    isLoading: boolean;
    agencyId: number;
    dispositionOptions: AttributeOption[];
    isPrimaryUnit?: boolean;
    initialValues?: DispositionsFormType | DispositionsForm;
    isEventClearing?: boolean;
    primaryDispositionEditable?: boolean;
}): React.ReactElement {
    const { unitLevelDispositionsEnabled, additionalDispositionsEnabled } = useEnabledFeatures();
    const bootstrapState = useBootstrapState();
    const form = useFormContext();

    const agencyIdToEventPanelAgencyConfiguration =
        bootstrapState.eventPanelConfiguration.agencyIdToEventPanelAgencyConfiguration;
    const agencyConfig = agencyIdToEventPanelAgencyConfiguration[agencyId];
    const dispositionIsRequiredByAgency = agencyConfig?.isDispositionRequiredToClose;

    const primaryDispositionIsRequired =
        primaryDispositionEditable &&
        isPrimaryDispositionRequired({
            isEventClearing,
            dispositionIsRequiredByAgency,
            unitLevelDispositionsEnabled,
            isPrimaryUnit,
        });
    // additional disposition field already exists before unit level disposition project. Always show additional dispositions when unitLevelDispositionsEnabled = false
    const showAdditionalDispositionsField = unitLevelDispositionsEnabled
        ? additionalDispositionsEnabled
        : true;

    // useFormContext is not stable across renders, so use setValue for this effect
    const { setValue } = form;

    useEffect(() => {
        // Seed the form from initialValues (including agencyEventId and the existing disposition to
        // prefill). Skip only for the unit-level-dispositions-OFF clear flow, which intentionally
        // starts blank; the edit-disposition flow (not event clearing) needs seeding in both ULD states.
        if (!unitLevelDispositionsEnabled && isEventClearing) {
            return;
        }
        if (isLoading || !initialValues) {
            return;
        }
        Object.entries(initialValues).forEach(([key, value]) => {
            if (value) {
                setValue(getFieldName(key, isEventClearing, unitLevelDispositionsEnabled), value);
            }
        });
    }, [unitLevelDispositionsEnabled, isLoading, initialValues, isEventClearing, setValue]);

    return (
        <VStack w="100%">
            <FormSelect
                control={form.control}
                label="Primary Disposition"
                name={getFieldName(
                    'primaryDisposition',
                    isEventClearing,
                    unitLevelDispositionsEnabled
                )}
                options={dispositionOptions}
                rules={{ required: primaryDispositionIsRequired && 'Required' }}
                isDisabled={isLoading || !primaryDispositionEditable}
                isLoading={isLoading}
            />
            {showAdditionalDispositionsField && (
                <FormSelect
                    isMultiple
                    control={form.control}
                    label="Additional Disposition"
                    name={getFieldName(
                        'additionalDispositions',
                        isEventClearing,
                        unitLevelDispositionsEnabled
                    )}
                    options={dispositionOptions}
                    isDisabled={isLoading}
                    isLoading={isLoading}
                />
            )}
            <FormTextarea
                control={form.control}
                label="Comments"
                name={getFieldName('comments', isEventClearing, unitLevelDispositionsEnabled)}
                isDisabled={isLoading}
                isLoading={isLoading}
            />
        </VStack>
    );
}
